import React, { useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useBatchResyncFormConfigMutation } from '@/features/config/configApi';
import { SCOPES } from '@/features/config/scopes';

/**
 * Resyncs every copy in the user's primary broker group (the rows whose Copied from cell has a
 * resync button) with the configs they were copied from, after a confirm. Rows whose source
 * no longer exists are left alone and counted in the toast. Shown only when there's a copy.
 */
const ResyncFormConfigButton = ({ configs, configType, disabled }) => {
	const dispatch = useDispatch();
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [batchResyncFormConfig] = useBatchResyncFormConfigMutation();

	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const docIds = configs
		.filter(
			(config) =>
				config.copiedFrom &&
				config.scope === SCOPES.BROKER_GROUP &&
				config.scopeId === userDetails?.brokerGroupId,
		)
		.map((config) => config.id);
	if (!docIds.length) return null;

	const rowCount = (count) => `${count} row${count === 1 ? '' : 's'}`;

	const handleResync = async () => {
		const result = await batchResyncFormConfig({ docIds, configType });
		setIsConfirmOpen(false);
		if (result.error) {
			dispatch(
				showToast({
					message: "Couldn't resync the config. Please try again.",
					error: true,
				}),
			);
			return;
		}
		const { docIds: resynced, missing } = result.data;
		dispatch(
			showToast(
				missing.length
					? {
							message: `Resynced ${rowCount(resynced.length)}. ${rowCount(missing.length)} couldn't be resynced because the config they were copied from no longer exists.`,
							error: true,
						}
					: { message: `Resynced ${rowCount(resynced.length)}`, success: true },
			),
		);
	};

	return (
		<>
			<Tooltip title='Replace every row with the current settings of the config it was copied from'>
				<span>
					<Button
						variant='table_actions'
						disabled={disabled}
						onClick={() => setIsConfirmOpen(true)}
					>
						{`Resync full config`}
					</Button>
				</span>
			</Tooltip>
			<ConfirmDialog
				openDialog={isConfirmOpen}
				handleClose={() => setIsConfirmOpen(false)}
				handleConfirm={handleResync}
				title='Resync full config?'
				content={`Replace the settings of ${rowCount(docIds.length)} with the current settings of the configs they were copied from?`}
				confirmText='Resync'
			/>
		</>
	);
};

export default ResyncFormConfigButton;
